import { z } from "zod";
import { e164Schema } from "./phone.js";
import { idSchema, multiLineTextSchema, singleLineTextSchema } from "./text.js";
// I WhatsApp nelle Impostazioni (piano M6, R1, Task 3; D2, D5, D25): i modelli dell'azienda e la riga «scrivi STOP», il WhatsApp
// di ogni persona e la scelta dei WhatsApp dell'azienda. Le rotte:
// - `GET /api/whatsapp/templates`: i sei modelli e la riga «scrivi STOP», letti da ogni persona attiva dell'azienda; la prima
//   lettura di un'azienda che non li ha li scrive con i testi di partenza, senza autore e senza evento (D5, ruling B1 ed E6).
//   `PUT /api/whatsapp/templates/:key` cambia nome e testo (questione 401: i modelli sono i sei di partenza, non se ne creano e non
//   se ne cancellano) e `PUT /api/whatsapp/stop-line` la riga (da 1 a 60 caratteri, non si toglie; questione 395), con
//   `settings.whatsapp_templates` e la versione letta. Con il WhatsApp ufficiale (R2, Task 8a) `POST
//   /api/whatsapp/templates/:key/submit` manda un modello a Meta per l'approvazione, e un testo o una riga cambiati rimandano da
//   sé i modelli che erano già su Meta;
// - `GET /api/whatsapp/senders`: le persone dell'azienda con il loro WhatsApp, lette da ogni persona attiva; `PUT
//   /api/whatsapp/senders/:membershipId` imposta il WhatsApp di una persona, con `manage_team` o `settings.whatsapp_sending` e
//   soltanto su chi ha un ruolo più basso di chi imposta (il Super admin e Gioia365 su tutti); da R9 anche su WhatsApp Agile, con il
//   suo codice, e `GET /api/whatsapp/senders/:membershipId/agile-pairing` rilegge il codice QR per collegare di nuovo il telefono;
// - `GET /api/settings/whatsapp-channels`: quali WhatsApp usa l'azienda, letti da ogni persona attiva, con il WhatsApp ufficiale
//   presso Meta (R2); `PUT` con `settings.whatsapp_channels` (fra i ruoli di partenza soltanto il Super admin, questione 403).
//   Spegnere un WhatsApp passa a mano le persone che lo usavano.
//
// Gli elenchi ripetono quelli del dominio (`@gioia365/domain`, `whatsapp.ts`) e dello schema operativo, che i contratti non
// importano: un test del server li confronta voce per voce.
/** I tre WhatsApp (D2): `manual` a mano, sempre acceso; `meta` il WhatsApp ufficiale; `agile` WhatsApp Agile. */
export const WHATSAPP_CHANNELS = ["manual", "meta", "agile"];
export const whatsAppChannelSchema = z.enum(WHATSAPP_CHANNELS);
/** I sei modelli dell'azienda (D5), nell'ordine del mock-up. */
export const WHATSAPP_TEMPLATE_KEYS = ["first_contact", "after_no_answer", "after_answer", "callback_confirmation", "disco_confirmation", "demo_confirmation"];
export const whatsAppTemplateKeySchema = z.enum(WHATSAPP_TEMPLATE_KEYS);
/** La categoria di un modello presso Meta: le conferme sono di utilità, il resto è marketing. */
export const WHATSAPP_TEMPLATE_CATEGORIES = ["utility", "marketing"];
/**
 * Lo stato del WhatsApp di una persona (`WHATSAPP_SENDER_STATES` dello schema operativo, tranne `removed`, che non si elenca): pronto,
 * in registrazione presso il fornitore, registrazione non riuscita. Il modo a mano è sempre pronto.
 */
export const WHATSAPP_SENDER_STATES = ["ready", "pending", "failed"];
/** I limiti dei testi: il nome di un modello, il suo testo (il corpo di un modello di Meta) e la riga «scrivi STOP» (il piè di pagina di Meta). */
export const WHATSAPP_TEMPLATE_NAME_MAX = 80;
export const WHATSAPP_TEMPLATE_BODY_MAX = 1024;
export const WHATSAPP_STOP_LINE_MAX = 60;
/**
 * Lo stato di un modello presso Meta (`WHATSAPP_TEMPLATE_META_STATUSES` dello schema operativo, piano M6, R2): in revisione,
 * approvato, rifiutato (con il motivo), sospeso da Meta per la qualità, spento o cancellato.
 */
export const WHATSAPP_TEMPLATE_META_STATUSES = ["pending", "approved", "rejected", "paused", "disabled"];
/**
 * Un modello dell'azienda: il nome e il testo di adesso, con le variabili fra graffe, la versione e quando è stato cambiato (ISO).
 * Con il WhatsApp ufficiale (piano M6, R2, Task 8a; ruling B4, A4c) anche come sta presso Meta:
 * - `metaStatus`: lo stato di Meta per il testo e la riga «scrivi STOP» di adesso; `null` per un modello mai mandato a Meta e per
 *   uno il cui testo (o la riga «scrivi STOP») è cambiato dopo l'invio: «da inviare», e si manda a mano;
 * - `metaCategory`: la categoria che Meta gli ha riconosciuto, anche diversa da `category` (la pagina lo dice); `null` finché non
 *   è su Meta; `metaRejectedReason`: il motivo di un rifiuto, come lo scrive Meta;
 * - `metaError`: il codice dell'ultimo invio a Meta non riuscito (`<code>` o `<code>:<subcode>` di Meta, o `no_response`),
 *   `null` dopo un invio riuscito; lo stato resta quello di prima;
 * - `approved`: il modello si può mandare dal WhatsApp ufficiale (Meta ha approvato il testo di adesso).
 */
export const whatsAppTemplateSchema = z.object({
    key: whatsAppTemplateKeySchema,
    name: z.string(),
    category: z.enum(WHATSAPP_TEMPLATE_CATEGORIES),
    body: z.string(),
    version: z.number().int(),
    updatedAt: z.string(),
    metaStatus: z.enum(WHATSAPP_TEMPLATE_META_STATUSES).nullable(),
    metaCategory: z.enum(WHATSAPP_TEMPLATE_CATEGORIES).nullable(),
    metaRejectedReason: z.string().nullable(),
    metaError: z.string().nullable(),
    approved: z.boolean(),
});
/**
 * Com'è andato il nuovo invio a Meta di un modello già mandato, dopo un testo o una riga «scrivi STOP» cambiati (piano M6, R2,
 * Task 8a; ruling A3b, B14): il salvataggio lo rimette subito «da inviare» (si manda a mano) e, dopo, lo rimanda da sé.
 * - `submitted`: Meta l'ha ricevuto, è in revisione;
 * - `failed`: Meta non l'ha accettato, o non ha risposto: `code` è il codice di Meta (lo stesso di `metaError`);
 * - `skipped`: non è partito, e `code` è il codice d'errore dell'API che l'invio avrebbe dato: `whatsapp_meta_not_configured`,
 *   `whatsapp_channel_not_enabled`, `whatsapp_meta_window_required` (l'azienda non è ancora collegata a Meta),
 *   `whatsapp_template_variable_at_edge` (questione 411), `version_conflict` (il testo è cambiato di nuovo nel frattempo: lo
 *   rimanda quel salvataggio, se il modello era già su Meta) o `internal_error` (un errore inatteso dopo il salvataggio, che resta
 *   fatto: quel modello e quelli dopo restano «da inviare»).
 */
export const WHATSAPP_TEMPLATE_RESUBMISSION_OUTCOMES = ["submitted", "failed", "skipped"];
export const whatsAppTemplateResubmissionSchema = z.object({
    key: whatsAppTemplateKeySchema,
    outcome: z.enum(WHATSAPP_TEMPLATE_RESUBMISSION_OUTCOMES),
    code: z.string().nullable(),
});
/**
 * `GET /api/whatsapp/templates`: i sei modelli nell'ordine del mock-up, la riga «scrivi STOP» dell'azienda con la sua versione, e se
 * chi guarda li può cambiare (`settings.whatsapp_templates`).
 */
export const whatsAppTemplatesResponseSchema = z.object({
    templates: z.array(whatsAppTemplateSchema),
    stopLine: z.string(),
    stopLineVersion: z.number().int(),
    canEdit: z.boolean(),
});
/** `PUT /api/whatsapp/templates/:key`: il nome (su una riga) e il testo (anche su più righe), con la versione letta. */
export const updateWhatsAppTemplateBodySchema = z.strictObject({
    name: singleLineTextSchema(WHATSAPP_TEMPLATE_NAME_MAX),
    body: multiLineTextSchema(WHATSAPP_TEMPLATE_BODY_MAX),
    expectedVersion: z.number().int().min(1),
});
/**
 * `PUT /api/whatsapp/templates/:key`: il modello com'è dopo e, se il testo di un modello già mandato a Meta è cambiato, com'è andato
 * il nuovo invio (`metaResubmission`, `null` altrimenti).
 */
export const whatsAppTemplateResponseSchema = z.object({ template: whatsAppTemplateSchema, metaResubmission: whatsAppTemplateResubmissionSchema.nullable() });
/** `POST /api/whatsapp/templates/:key/submit` («Invia a Meta per l'approvazione», piano M6, R2, Task 8a): il modello com'è dopo. */
export const whatsAppTemplateSubmitResponseSchema = z.object({ template: whatsAppTemplateSchema });
/**
 * `PUT /api/whatsapp/stop-line`: la riga su una riga sola, da 1 a 60 caratteri dopo il trim (tab e a capo diventano uno spazio,
 * quindi una riga fatta soltanto di spazi, tab o a capo è vuota e si rifiuta), con la versione letta.
 */
export const updateWhatsAppStopLineBodySchema = z.strictObject({
    stopLine: singleLineTextSchema(WHATSAPP_STOP_LINE_MAX),
    expectedVersion: z.number().int().min(1),
});
/**
 * `PUT /api/whatsapp/stop-line`: la riga com'è dopo, con la sua versione, e com'è andato il nuovo invio a Meta di ogni modello che
 * c'era già (la riga è il piè di pagina dei modelli su Meta, D25): un elemento per modello rimandato, nell'ordine del mock-up, vuoto
 * se nessuno era su Meta o la riga non è cambiata.
 */
export const whatsAppStopLineResponseSchema = z.object({
    stopLine: z.string(),
    stopLineVersion: z.number().int(),
    metaResubmissions: z.array(whatsAppTemplateResubmissionSchema),
});
/**
 * Perché un WhatsApp ufficiale di Meta non è pronto (piano M6, R2, Task 7a2; D9), nell'ordine in cui si dice il primo:
 * - `meta_not_configured`: mancano le quattro variabili di Meta sul server;
 * - `failed`: la registrazione su Meta non è riuscita (il codice è in `failureCode`);
 * - `not_connected`: il collegamento dell'azienda presso Meta non è `connected` (o la registrazione non è finita);
 * - `caller_removed`: il numero è un cellulare del parco che non è più `verified` («Il cellulare è stato tolto dal parco»).
 * Pronto, o un WhatsApp che non è l'ufficiale: `null`.
 */
export const WHATSAPP_SENDER_NOT_READY_REASONS = ["meta_not_configured", "failed", "not_connected", "caller_removed"];
/**
 * L'ultimo stato conosciuto del canale di un WhatsApp Agile (piano M6, R9; `whatsapp_channel_health`), scritto dal controllo prima di
 * ogni invio, dal salvataggio del codice e dal giro di ogni ora; mai dalla rete quando si legge. `healthy` vale `active` e `working`
 * non falso; `working` è `null` quando il fornitore non lo dice. `checkedAt` l'ora dell'ultima risposta del fornitore, `downSince` da
 * quando il canale non consegna (vuoto se consegna), `lastOkAt` l'ultima volta che consegnava, `errorCode` perché non consegna
 * (`inactive`, `not_working` o la risposta d'errore del fornitore, `http_<stato>`). Una risposta che non arriva non lo cambia. Un
 * «giù» tiene i messaggi a mano soltanto per 10 minuti da `checkedAt` (`storedChannelDownHolds` e `STORED_CHANNEL_DOWN_HOLD_MS` del
 * dominio): dopo, la finestra offre l'invio e l'invio richiede il canale al fornitore.
 */
export const whatsAppAgileHealthSchema = z.object({
    healthy: z.boolean(),
    active: z.boolean(),
    working: z.boolean().nullable(),
    checkedAt: z.string(),
    downSince: z.string().nullable(),
    lastOkAt: z.string().nullable(),
    errorCode: z.string().nullable(),
});
/**
 * Il WhatsApp di una persona: il modo, il numero in E.164 (dice da quale telefono scrive), lo stato, il motivo di una registrazione
 * su Meta non riuscita (`failureCode`, soltanto il codice: del passo o di Meta, nella forma `<code>` o `<code>:<subcode>`; vuoto negli
 * altri stati), perché un WhatsApp ufficiale non è pronto (`notReadyReason`) e la versione. Per un WhatsApp Agile (R9) anche lo stato
 * del suo canale (`agileHealth`, `null` se non è mai stato controllato); per gli altri modi il campo non c'è. Il codice di Agile non
 * c'è mai: si rilegge soltanto come codice QR, con `GET /api/whatsapp/senders/:membershipId/agile-pairing`.
 */
export const whatsAppSenderSchema = z.object({
    id: z.string(),
    channel: whatsAppChannelSchema,
    e164: z.string(),
    state: z.enum(WHATSAPP_SENDER_STATES),
    failureCode: z.string().nullable(),
    notReadyReason: z.enum(WHATSAPP_SENDER_NOT_READY_REASONS).nullable(),
    version: z.number().int(),
    agileHealth: whatsAppAgileHealthSchema.nullable().optional(),
});
/**
 * Una persona dell'azienda non archiviata, con il suo WhatsApp (`null` se non ne ha uno: scrive a mano), se chi guarda glielo può
 * impostare (`canSet`: la funzione e il ruolo della persona più basso del suo, o il Super admin e Gioia365) e se può registrarle un
 * numero su Meta («Registra su Meta e salva», `canRegisterMeta`): la stessa regola di `canSet` (questione 408), con il WhatsApp
 * ufficiale acceso per l'azienda e collegato a Gioia365 (le variabili di Meta).
 */
export const whatsAppSenderPersonSchema = z.object({
    membershipId: z.string(),
    displayName: z.string(),
    status: z.enum(["active", "suspended"]),
    sender: whatsAppSenderSchema.nullable(),
    canSet: z.boolean(),
    canRegisterMeta: z.boolean(),
});
/** `GET /api/whatsapp/senders`: le persone in ordine di nome e se chi guarda ha la funzione per impostarle (`canEdit`). */
export const whatsAppSendersResponseSchema = z.object({ people: z.array(whatsAppSenderPersonSchema), canEdit: z.boolean() });
/** Il codice di un numero su WhatsApp Agile, come lo dà il pannello del fornitore: lettere, cifre, `_` e `-`. */
export const WHATSAPP_AGILE_CODE_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
/**
 * `PUT /api/whatsapp/senders/:membershipId`: il modo e il numero, con la versione letta del WhatsApp della persona; senza versione
 * chi imposta dice che la persona non ne ha ancora uno. Un numero che è già di un'altra persona è `409 whatsapp_number_taken`, con
 * `details.holderDisplayName`; un modo che l'azienda non usa, o che non esiste nel codice (Agile senza la sua chiave sul server, il
 * WhatsApp ufficiale senza le variabili di Meta), `409 whatsapp_channel_not_enabled`. Il WhatsApp ufficiale non si imposta qui: si
 * registra su Meta («Registra su Meta e salva»), quindi `channel: "meta"` risponde `409 whatsapp_meta_window_required`.
 * WhatsApp Agile (R9) vuole il suo codice (`agileCode`, soltanto con `channel: "agile"`; senza, dopo il controllo del modo, `422
 * validation_failed` con `fields: ["agileCode"]`) e il numero con il prefisso del paese: il server chiede al fornitore se quel codice è
 * attivo per quel numero e consegna, e salva soltanto allora; altrimenti
 * `409 whatsapp_agile_code_not_recognised` (il codice non è di quel numero), `409 whatsapp_agile_not_paired` (il telefono non risulta
 * collegato: il codice QR va inquadrato prima) o `502 whatsapp_agile_unreachable` (il fornitore non risponde). Salvare di nuovo lo
 * stesso codice ricontrolla il canale («Ho inquadrato il codice, salva» dopo un nuovo collegamento).
 */
export const setWhatsAppSenderBodySchema = z
    .strictObject({
    channel: whatsAppChannelSchema,
    e164: e164Schema,
    expectedVersion: z.number().int().min(1).optional(),
    agileCode: z.string().regex(WHATSAPP_AGILE_CODE_PATTERN).optional(),
})
    .refine((body) => body.agileCode === undefined || body.channel === "agile", { path: ["agileCode"], message: "agileCode goes with the agile channel only" });
/**
 * `GET /api/whatsapp/senders/:membershipId/agile-pairing` (piano M6, R9), con «Invio dei WhatsApp» (`settings.whatsapp_sending`) e la
 * regola di «Imposta» su quella persona: il link del codice QR per collegare di nuovo il telefono del WhatsApp Agile della persona
 * (`pairingUrl`, sull'unico indirizzo del fornitore che completa il collegamento). Una persona senza un WhatsApp Agile, o di un'altra
 * azienda, è `404`. `recentlyTaken`: quanti WhatsApp Agile partiti da quel numero il fornitore ha preso in carico nelle ultime 24 ore;
 * `mayReleaseQueued` (con almeno uno) dice alla pagina di avvisare che collegando di nuovo il telefono il fornitore manda subito quelli
 * che teneva in coda (li tiene 24 ore e li ritenta da sé). La risposta non si tiene in cache.
 */
export const whatsAppAgilePairingResponseSchema = z.object({
    pairingUrl: z.string(),
    recentlyTaken: z.number().int().min(0),
    mayReleaseQueued: z.boolean(),
});
/** `PUT /api/whatsapp/senders/:membershipId`: la persona com'è dopo. */
export const whatsAppSenderPersonResponseSchema = z.object({ person: whatsAppSenderPersonSchema });
/**
 * Il collegamento del WhatsApp ufficiale dell'azienda presso Meta (`whatsapp_connections` del database di piattaforma, D8), come lo
 * vede il browser: nessun segreto, né la chiave dei webhook, né gli identificativi dell'account presso Meta.
 * - `state`: `not_connected` la registrazione non è finita, `connected` gettone salvato e webhook iscritti, `failed` un passo non è
 *   riuscito; `setupStep` l'ultimo passo finito e `setupError` il codice dell'ultimo passo non riuscito («Riprova» riprende da lì);
 * - il riquadro «Business Manager di Meta» (questione 416), letto da Meta alla registrazione e dal giro dei 15 minuti: il nome
 *   dell'azienda su Meta, se Meta l'ha verificata, quanti numeri ha registrati (`null` finché non si è letto) e il tetto (2, 20 dopo
 *   la verifica, D9), quando si è letto (ISO).
 * Gli elenchi ripetono quelli dello schema di piattaforma, che i contratti non importano: un test del server li confronta.
 */
export const WHATSAPP_CONNECTION_STATES = ["not_connected", "connected", "failed"];
export const WHATSAPP_SETUP_STEPS = ["none", "token", "webhook", "subscribed"];
export const whatsAppConnectionViewSchema = z.object({
    state: z.enum(WHATSAPP_CONNECTION_STATES),
    setupStep: z.enum(WHATSAPP_SETUP_STEPS),
    setupError: z.string().nullable(),
    businessName: z.string().nullable(),
    businessVerified: z.boolean(),
    registeredNumbers: z.number().int().nullable(),
    numberCap: z.number().int(),
    readAt: z.string().nullable(),
    version: z.number().int(),
});
/**
 * Ciò che serve al browser per «Registra su Meta e salva» (piano M6, R2, ruling B18): `available` se le quattro variabili di Meta
 * ci sono (senza, «WhatsApp ufficiale di Meta» si vede ma resta spento con «Il WhatsApp ufficiale non è ancora collegato a
 * Gioia365»); con le variabili i tre valori pubblici della finestra di Meta (l'app, la configurazione di Embedded Signup e la
 * versione della Graph API), altrimenti `null`; il collegamento dell'azienda, `null` finché nessuno ha cominciato a registrare un
 * numero o a impostare i numeri ammessi.
 */
export const whatsAppMetaSignupSchema = z.object({
    available: z.boolean(),
    appId: z.string().nullable(),
    configId: z.string().nullable(),
    graphVersion: z.string().nullable(),
    connection: whatsAppConnectionViewSchema.nullable(),
});
/**
 * WhatsApp Agile sul server (piano M6, R9): `available` se la sua chiave c'è (senza, «WhatsApp Agile» si vede ma resta spento: «non
 * disponibile», e non si accende); con la chiave l'inizio del link del codice QR (`pairingBaseUrl`, l'unico indirizzo del fornitore
 * che completa il collegamento): il link di un codice è `pairingBaseUrl` seguito dal codice, così la pagina mostra il codice QR prima
 * di salvarlo. Senza la chiave `null`.
 */
export const whatsAppAgileSignupSchema = z.object({ available: z.boolean(), pairingBaseUrl: z.string().nullable() });
/**
 * `GET /api/settings/whatsapp-channels` e la risposta del `PUT`: quali WhatsApp usa l'azienda (`manual` sempre, questione 391), la
 * versione delle impostazioni dell'azienda (la stessa del calendario e della sequenza: `409 version_conflict` se è cambiata), se chi
 * guarda la può cambiare (`settings.whatsapp_channels`), il WhatsApp ufficiale presso Meta (`metaSignup`; `meta` resta la scelta
 * dell'azienda) e WhatsApp Agile sul server (`agileSignup`, R9). Il WhatsApp ufficiale si accende soltanto con le variabili di Meta,
 * Agile soltanto con la sua chiave.
 */
export const whatsAppChannelsSettingsSchema = z.object({
    manual: z.literal(true),
    meta: z.boolean(),
    agile: z.boolean(),
    version: z.number().int(),
    canEdit: z.boolean(),
    metaSignup: whatsAppMetaSignupSchema,
    agileSignup: whatsAppAgileSignupSchema,
});
/**
 * `PUT /api/settings/whatsapp-channels`: i due WhatsApp automatici, accesi o spenti, con la versione letta delle impostazioni.
 * Accendere il WhatsApp ufficiale senza le variabili di Meta, o Agile senza la sua chiave, è `409 whatsapp_channel_not_enabled`; una
 * scelta già accesa si salva di nuovo (si cambia l'altro senza spegnerla). Spegnere un WhatsApp passa a mano chi lo usava (WA-2).
 */
export const saveWhatsAppChannelsBodySchema = z.strictObject({ meta: z.boolean(), agile: z.boolean(), expectedVersion: z.number().int().min(1) });
/** L'identificativo di un account di Meta (il WhatsApp Business Account): cifre. */
const META_ACCOUNT_ID = /^[0-9]{1,32}$/;
/** Il codice di Embedded Signup: un segreto di Meta che vale 30 secondi, di caratteri visibili e senza spazi. */
const META_SIGNUP_CODE = /^[\x21-\x7e]{1,4096}$/;
/**
 * `POST /api/whatsapp/meta-signup`, «Registra su Meta e salva» (piano M6, R2, Task 7a2; ruling A2b, A2c, B5; questioni 408 e 412): la
 * persona, il numero scelto (il cellulare dell'ufficio, quello che la persona sceglie anche nella finestra di Meta) e la versione letta
 * del suo WhatsApp (senza, chi registra dice che la persona non ne ha ancora uno, come `PUT /api/whatsapp/senders/:membershipId`).
 * - Senza `code`: se l'azienda ha già il gettone di Meta e il numero è fra quelli del suo WhatsApp Business Account, la registrazione
 *   salta la finestra di Meta; altrimenti `409 whatsapp_meta_window_required` e il browser apre la finestra. È anche «Riprova» dopo un
 *   passo non riuscito, con la versione che il `502` ha detto (`details.version`): riprende dal passo dopo il gettone.
 * - Con `code` e `wabaId`, dalla finestra di Meta (vanno insieme): il codice si scambia con il gettone, poi la registrazione intera.
 */
export const whatsAppMetaSignupBodySchema = z
    .strictObject({
    membershipId: idSchema,
    e164: e164Schema,
    expectedVersion: z.number().int().min(1).optional(),
    code: z.string().regex(META_SIGNUP_CODE).optional(),
    wabaId: z.string().regex(META_ACCOUNT_ID).optional(),
})
    .refine((body) => (body.code === undefined) === (body.wabaId === undefined), { path: ["wabaId"], message: "code and wabaId go together" });
/**
 * Il passo della registrazione su Meta che non è riuscito (`details.step` di `502 whatsapp_signup_failed`): lo scambio del codice e il
 * gettone, la chiave e il gettone di verifica dei webhook (oggi soltanto con un lock perso), l'iscrizione ai webhook del WhatsApp
 * Business Account, la lettura dei suoi numeri. Il `502` porta anche `details.failureCode` (il codice di Meta, `<code>` o
 * `<code>:<subcode>`, o dell'adattatore e del server: `no_response`, `invalid_response`, `lock_lost`…) e `details.version`, la versione
 * di adesso del WhatsApp della persona (`null` se non ne ha uno), da rimandare come `expectedVersion` con «Riprova». Il WhatsApp della
 * persona diventa `failed` soltanto se non c'era o se era già l'ufficiale non pronto su quel numero: un WhatsApp che funziona resta.
 */
export const WHATSAPP_SIGNUP_STEPS = ["token", "webhook", "subscribed", "phone_number"];
/** `POST /api/whatsapp/meta-signup` riuscito: la persona con il suo WhatsApp ufficiale e il collegamento dell'azienda dopo. */
export const whatsAppMetaSignupResponseSchema = z.object({ person: whatsAppSenderPersonSchema, connection: whatsAppConnectionViewSchema });
// ---------------------------------------------------------------------------------------------------------------
// Preparare un WhatsApp, «Sì, inviato», «Non inviare WhatsApp» e la Timeline (piano M6, R1, Task 3b; D3, D4, D5, D25;
// ruling B2, B5, B9, E4, E7, E8; questioni 400 e 402):
// - `POST /api/leads/:leadId/whatsapp/prepare`: chi ha «Mandare WhatsApp» (`whatsapp_send`), su un Lead del suo perimetro di
//   modifica, fuori da «Guarda come…» e non dal contesto di Gioia365, prepara il messaggio di un modello per uno dei cellulari del
//   Lead. Il server riempie le variabili, mette in fondo la riga «scrivi STOP» dell'azienda e salva il testo esatto che il Lead
//   riceve, con il link `wa.me` (D4); un Lead che non riceve WhatsApp è `409 whatsapp_blocked` con il motivo;
// - `POST /api/whatsapp/messages/:messageId/declare-sent`: «Sì, inviato», soltanto l'autore, una volta;
// - `PUT /api/leads/:leadId/whatsapp-opt-out`: l'interruttore «Non inviare WhatsApp» della scheda (D25, questione 392);
// - la scheda del Lead porta `whatsapp` (`leadWhatsAppSchema`), la Timeline un elemento `whatsapp` per messaggio (`timeline.ts`);
// - dal piano M6, R2 (Task 9a), `POST /api/leads/:leadId/whatsapp/send`: «Invia il WhatsApp» dal WhatsApp ufficiale (più sotto).
/**
 * Perché un Lead non riceve WhatsApp (`details.reason` di `409 whatsapp_blocked`, e i primi motivi di `whatsapp.reason` della
 * scheda), nell'ordine in cui si dicono: lo status blocca le chiamate, nessun cellulare attivo non segnato errato, «Non inviare
 * WhatsApp» acceso. In R1 il consenso non si valuta (questione 400): `consent` del dominio non arriva mai qui.
 */
export const WHATSAPP_LEAD_BLOCKS = ["do_not_contact", "no_mobile", "whatsapp_opt_out"];
/**
 * Perché «WhatsApp» è spento nella scheda, nell'ordine in cui si dice il primo, come per «Chiama»: prima il Lead
 * (`WHATSAPP_LEAD_BLOCKS`), poi chi guarda: senza «Mandare WhatsApp», un Lead che non può modificare, «Guarda come…», il contesto di
 * Gioia365 (nei due ultimi casi la preparazione risponde `403 permission_denied` con lo stesso motivo).
 */
export const WHATSAPP_UNAVAILABLE_REASONS = [...WHATSAPP_LEAD_BLOCKS, "no_feature", "not_editable", "commercial_view", "platform_context"];
/** Da dove è stato acceso «Non inviare WhatsApp»: lo «STOP» del Lead (R3) o l'interruttore della scheda. */
export const LEAD_WHATSAPP_OPT_OUT_SOURCES = ["lead_stop", "manual"];
/**
 * Il WhatsApp di chi guarda come lo legge `whatsappRoute` (piano M6, R2, Task 9a; ruling A4c): il modo, se è pronto (`senderReady`,
 * con il collegamento dell'azienda e il cellulare del parco; per WhatsApp Agile, R9, con la chiave, il codice e lo stato salvato del
 * canale, che lo tiene non pronto soltanto per un «giù» di meno di 10 minuti, `storedChannelDownHolds` del dominio) e se l'azienda usa
 * quel modo; il suo numero (Task 9b), che la finestra
 * dice con il WhatsApp ufficiale («Parte dal WhatsApp ufficiale, dal tuo numero …», mock-up `mockup.js`, `syncWaMode`). `null` per
 * chi non ne ha uno (scrive a mano) e per il contesto di Gioia365.
 */
export const leadWhatsAppSendViaSchema = z.object({ channel: whatsAppChannelSchema, ready: z.boolean(), enabledByCompany: z.boolean(), e164: e164Schema });
/**
 * Il WhatsApp nella scheda del Lead (ruling B5), con la stessa regola della preparazione:
 * - `available` e `reason`: se «WhatsApp» è acceso per chi guarda, altrimenti il primo motivo (`WHATSAPP_UNAVAILABLE_REASONS`);
 * - `phoneIds`: i cellulari attivi non segnati errati, nell'ordine della scheda: a chi si può scrivere («A chi scrivi»; il primo è
 *   quello di partenza della preparazione);
 * - `optOut`: «Non inviare WhatsApp» acceso (quando, da dove e, per l'interruttore di una persona dell'azienda, da chi), `null` spento;
 * - `canSwitchOptOutOn`: chi può modificare il Lead lo accende; `canSwitchOptOutOff`: lo spegne soltanto chi ha anche «Gestire il
 *   team» (`manage_team`: fra i ruoli di partenza Super user, Admin e Super admin; questione 392);
 * - `sendVia` (piano M6, R2, Task 9a; ruling A4c): il WhatsApp di chi guarda (`leadWhatsAppSendViaSchema`), con cui la finestra
 *   calcola con `whatsappRoute`, prima del clic e dagli stessi fatti del server, se il pulsante è «Invia il WhatsApp» o il ripiego a
 *   mano con il perché (insieme a `approved` della vista dei modelli);
 * - `todayCount`: quanti WhatsApp questo Lead ha già ricevuto oggi, nel giorno del fuso dell'azienda (dichiarati a mano, presi in
 *   carico, inviati, consegnati, letti; non i preparati né i non riusciti; dal piano M6, R3, nemmeno le risposte di testo libero,
 *   questione 422): da 2 la finestra avvisa prima dell'invio (questione 413).
 */
export const leadWhatsAppSchema = z.object({
    available: z.boolean(),
    reason: z.enum(WHATSAPP_UNAVAILABLE_REASONS).nullable(),
    phoneIds: z.array(z.string()),
    optOut: z.object({ at: z.string(), source: z.enum(LEAD_WHATSAPP_OPT_OUT_SOURCES), byDisplayName: z.string().nullable() }).nullable(),
    canSwitchOptOutOn: z.boolean(),
    canSwitchOptOutOff: z.boolean(),
    sendVia: leadWhatsAppSendViaSchema.nullable(),
    todayCount: z.number().int().min(0),
});
/** Una scheda di prima del Task 3b, senza `whatsapp`: nessun WhatsApp e nessun interruttore. */
export const NO_LEAD_WHATSAPP = {
    available: false,
    reason: null,
    phoneIds: [],
    optOut: null,
    canSwitchOptOutOn: false,
    canSwitchOptOutOff: false,
    sendVia: null,
    todayCount: 0,
};
/**
 * Perché un messaggio ripiega a mano (`whatsappRoute` del dominio): nessun WhatsApp della persona, canale spento, numero non pronto,
 * modello non approvato, una variabile del modello senza valore per questo Lead (dal WhatsApp ufficiale il testo non si ritocca: a
 * mano la persona lo completa; piano M6, R2, Task 9a). Da R9 i controlli di WhatsApp Agile al momento dell'invio, nel loro ordine: il
 * cellulare del Lead non è fra i numeri ammessi (fuori dalla produzione, INFRA-8), il tetto del giorno (l'invio lo dice con `409
 * whatsapp_daily_cap`, la finestra lo può dire prima del clic), il ritmo del numero (con `nextAt`), il canale che non consegna, il
 * fornitore che non risponde.
 */
export const WHATSAPP_FALLBACK_REASONS = [
    "sender_not_ready",
    "template_not_approved",
    "no_sender",
    "channel_disabled",
    "missing_values",
    "recipient_not_allowed",
    "daily_cap",
    "paced",
    "channel_down",
    "provider_unreachable",
];
/**
 * Come parte il messaggio secondo `whatsappRoute`: con il WhatsApp della persona (`send`, con il suo modo) o a mano per ripiego, con
 * il perché da dire nella finestra. Nella preparazione (`prepare`, «Apri WhatsApp») il messaggio è sempre a mano, anche quando la
 * scelta è `send` con il WhatsApp ufficiale: la persona ha scelto di scrivere dal suo telefono (piano M6, R2, Task 9a). Con il ritmo di
 * WhatsApp Agile (`paced`, R9) `nextAt` dice da quando può partire il prossimo dal numero di chi scrive (ISO); con gli altri motivi
 * non c'è.
 */
export const whatsAppRouteSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("send"), channel: whatsAppChannelSchema }),
    z.object({ kind: z.literal("fallback_manual"), reason: z.enum(WHATSAPP_FALLBACK_REASONS), nextAt: z.string().optional() }),
]);
/**
 * `POST /api/leads/:leadId/whatsapp/prepare`: il modello, il cellulare del Lead (`phoneId`, uno di `whatsapp.phoneIds` della scheda;
 * senza, il primo), la chiamata da cui nasce («Invia WhatsApp» dopo l'esito, una chiamata di questo Lead) e il testo ritoccato nella
 * finestra (`editedText`). Il browser apre il link da sé (ruling E7) e manda come `editedText` il testo che ci ha messo: così il
 * messaggio salvato è quello che il Lead riceve. La riga «scrivi STOP» la mette sempre il server (una volta, anche se il testo la ha
 * già); un testo con una variabile che resta vuota (`{giorno e ora}` senza un richiamo aperto) non si prepara: `422 validation_failed`
 * con `details.missing` (D5: la finestra chiede di completarlo).
 */
export const prepareWhatsAppBodySchema = z.strictObject({
    templateKey: whatsAppTemplateKeySchema,
    phoneId: idSchema.optional(),
    callId: idSchema.optional(),
    editedText: multiLineTextSchema(WHATSAPP_TEMPLATE_BODY_MAX).optional(),
});
/**
 * La risposta (201): il messaggio preparato (riaprire la stessa finestra riusa questo `messageId`, ruling E8), il testo esatto con
 * la riga «scrivi STOP» in fondo, il link `https://wa.me/<numero>?text=<testo>` (il controllo di quello che il browser ha aperto),
 * come parte (`route`) e, dal piano M6, R2 (Task 9a; questione 413), se il Lead aveva già ricevuto oggi 2 WhatsApp o più
 * (`dailyCapWarning`: la finestra lo dice anche a mano; una conferma conta ma non avvisa).
 */
export const prepareWhatsAppResponseSchema = z.object({
    messageId: z.string(),
    text: z.string(),
    link: z.string(),
    route: whatsAppRouteSchema,
    dailyCapWarning: z.boolean(),
});
/**
 * Lo stato di un messaggio (`WHATSAPP_MESSAGE_STATUSES` dello schema operativo, D3): a mano `prepared` → `declared_sent`; dal
 * WhatsApp ufficiale `queued` («Preso in carico da Meta») → `sent` → `delivered` → `read`, oppure `failed`, che dice soltanto Meta;
 * da WhatsApp Agile (R9) `queued` finché il fornitore non risponde, poi `sent` («Preso in carico da WhatsApp Agile», e lì resta)
 * oppure `failed`.
 */
export const WHATSAPP_MESSAGE_STATUSES = ["prepared", "declared_sent", "queued", "sent", "delivered", "read", "failed", "received"];
export const whatsAppMessageStatusSchema = z.enum(WHATSAPP_MESSAGE_STATUSES);
/**
 * Che cosa ha mandato il Lead in un messaggio in arrivo (`WHATSAPP_CONTENT_KINDS` del dominio, piano M6, R3; questione 425): un testo,
 * oppure un'immagine, un audio, un video, un documento, uno sticker, una posizione, un contatto o un altro tipo, che Gioia365 non
 * scarica: si aprono sul telefono.
 */
export const WHATSAPP_CONTENT_KINDS = ["text", "image", "audio", "video", "document", "sticker", "location", "contacts", "other"];
export const whatsAppContentKindSchema = z.enum(WHATSAPP_CONTENT_KINDS);
/**
 * Un messaggio in uscita che Gioia365 manda da sé, senza una persona che lo scrive (`WHATSAPP_AUTOMATIC_MESSAGES` dello schema
 * operativo, piano M6, R3, passo 11b; questione 420): la conferma dello «STOP» del Lead. Nella Timeline «Gioia365 (automatico)».
 */
export const WHATSAPP_AUTOMATIC_MESSAGES = ["stop_confirmation"];
export const whatsAppAutomaticMessageSchema = z.enum(WHATSAPP_AUTOMATIC_MESSAGES);
// ---------------------------------------------------------------------------------------------------------------
// «Invia il WhatsApp» dal WhatsApp ufficiale di Meta (piano M6, R2, Task 9a; D3, D5, D12, D19; ruling A4, B2, E4, E5; questioni 413,
// 414 e 415): `POST /api/leads/:leadId/whatsapp/send`, con le stesse regole di chi lo può fare della preparazione.
/**
 * Il modello, l'identificativo della richiesta del browser (uno per Lead, modello e cellulare finché il server non risponde con il suo
 * esito, E4: lo stesso clic due volte o un nuovo tentativo dopo una rete caduta, anche a finestra riaperta, trovano lo stesso
 * messaggio, e niente parte due volte), il cellulare del
 * Lead (`phoneId`, uno di `whatsapp.phoneIds` della scheda; senza, il primo) e la chiamata da cui nasce. Dal WhatsApp ufficiale il
 * testo non si ritocca: è quello del modello approvato, con le variabili del Lead (`editedText` lì è `422 validation_failed`). Con
 * WhatsApp Agile (R9, WA-6) il testo si ritocca: `editedText` è il testo della finestra, con le variabili fra graffe ancora da riempire;
 * il server le riempie e mette in fondo la riga «scrivi STOP» (una variabile che resta vuota è `422 validation_failed` con
 * `details.missing`, come nella preparazione).
 */
export const sendWhatsAppBodySchema = z.strictObject({
    templateKey: whatsAppTemplateKeySchema,
    clientRequestId: z.uuid(),
    phoneId: idSchema.optional(),
    callId: idSchema.optional(),
    editedText: multiLineTextSchema(WHATSAPP_TEMPLATE_BODY_MAX).optional(),
});
/**
 * La risposta (200):
 * - `route`: la scelta di `whatsappRoute`. Con `send` dal WhatsApp ufficiale il messaggio è partito verso Meta: `messageId` e
 *   `status` dicono il messaggio (`queued` «Preso in carico da Meta», anche quando Meta non ha risposto e l'esito si saprà dopo;
 *   `failed` quando Meta l'ha rifiutato, con il codice e il motivo nella Timeline; nessun nuovo tentativo da sé). Con `send` da
 *   WhatsApp Agile (R9) `sent` vuol dire soltanto «Preso in carico da WhatsApp Agile» (mai consegnato né letto: Agile non lo dice);
 *   `failed` quando il fornitore non l'ha preso, con il suo codice, oppure con `no_response` quando la risposta non è arrivata (può
 *   essere partito: il fornitore ritenta da sé); mai un nuovo tentativo da Gioia365. Con `fallback_manual` (o il WhatsApp della
 *   persona a mano) nessun messaggio è stato scritto né mandato: `messageId` e `status` sono `null`, e la finestra dice il perché e
 *   offre «Apri WhatsApp». Il tetto di Agile non è un ripiego ma `409 whatsapp_daily_cap` (WA-10: le conferme passano sempre);
 * - `dailyCapWarning`: il Lead aveva già ricevuto oggi 2 WhatsApp o più (questione 413): il messaggio parte lo stesso, la risposta
 *   ripete l'avviso che la finestra ha detto prima del clic; una conferma (modello di categoria `utility`) conta ma non avvisa.
 * La stessa richiesta (Lead e `clientRequestId`) risponde con lo stesso messaggio e lo stato di adesso.
 */
export const sendWhatsAppResponseSchema = z.object({
    route: whatsAppRouteSchema,
    messageId: z.string().nullable(),
    status: whatsAppMessageStatusSchema.nullable(),
    dailyCapWarning: z.boolean(),
});
/** `POST /api/whatsapp/messages/:messageId/declare-sent`: il messaggio dopo («dichiarato inviato», con l'ora della preparazione e quella di adesso). */
export const declareWhatsAppSentResponseSchema = z.object({
    message: z.object({ id: z.string(), leadId: z.string(), status: whatsAppMessageStatusSchema, preparedAt: z.string(), statusAt: z.string() }),
});
/**
 * `PUT /api/leads/:leadId/whatsapp-opt-out`: «Non inviare WhatsApp» acceso o spento, con la versione letta del Lead (cambia il Lead:
 * la versione sale di uno). La risposta è in `leads.ts` (`whatsAppOptOutResponseSchema`), con il Lead della scheda.
 */
export const setWhatsAppOptOutBodySchema = z.strictObject({ on: z.boolean(), expectedVersion: z.number().int().min(1) });
// ---------------------------------------------------------------------------------------------------------------
// I numeri ammessi dell'azienda (piano M6, R2, Task 7a1; D19, questione 414): fuori dalla produzione, o con
// `WHATSAPP_RECIPIENT_ALLOWLIST_REQUIRED=true`, i WhatsApp automatici partono soltanto verso questi numeri. Li imposta il gestore
// della piattaforma: `GET`/`PUT /api/platform/organizations/:id/whatsapp/allowed-recipients`, come i numeri ammessi della telefonia.
/** Al massimo venti numeri ammessi: i telefoni del team che prova. */
export const MAX_WHATSAPP_ALLOWED_RECIPIENTS = 20;
/**
 * I numeri ammessi in E.164, nell'ordine in cui si sono salvati, la versione del collegamento WhatsApp dell'azienda (`null` finché
 * il collegamento non c'è: nessun numero ammesso, quindi dove sono richiesti nessun WhatsApp automatico parte) e se in questo
 * ambiente sono richiesti (`required`: sempre fuori dalla produzione, in produzione con `WHATSAPP_RECIPIENT_ALLOWLIST_REQUIRED=true`).
 */
export const whatsAppAllowedRecipientsResponseSchema = z.object({
    allowedRecipients: z.array(z.string()),
    version: z.number().int().nullable(),
    required: z.boolean(),
});
/**
 * `PUT`: da zero a venti numeri in E.164 (il server ne tiene uno solo per numero ripetuto), con la versione letta; senza versione chi
 * imposta dice che il collegamento non c'è ancora (`409 version_conflict` con quella di adesso, altrimenti).
 */
export const saveWhatsAppAllowedRecipientsBodySchema = z.strictObject({
    allowedRecipients: z.array(e164Schema).max(MAX_WHATSAPP_ALLOWED_RECIPIENTS),
    expectedVersion: z.number().int().min(1).optional(),
});
// ---------------------------------------------------------------------------------------------------------------
// «Rispondi su WhatsApp» (piano M6, R3, passo 12a; D3, D11, D25; questioni 419, 421 e 422; ruling R3 «La risposta»):
// `POST /api/leads/:leadId/whatsapp/reply`, da «Conversazioni → WhatsApp», a un messaggio del Lead. Parte dal numero dell'azienda
// che ha ricevuto quel messaggio (questione 419), anche quando risponde un'altra persona: l'autore è chi scrive. Lo può fare chi
// vede quella conversazione (l'area `conversations`) e ha «Mandare WhatsApp», su un Lead che può modificare, fuori dalla «Guarda
// come…» e non dal contesto di Gioia365.
/** Il testo più lungo che Meta accetta in un messaggio, compresa la riga «scrivi STOP» in fondo. */
export const WHATSAPP_REPLY_TEXT_MAX = 4096;
/**
 * Perché una risposta non parte (`details.reason` di `409 whatsapp_blocked`): il Lead non si può contattare o ha «Non inviare
 * WhatsApp» acceso, oppure un testo libero dopo le 24 ore dall'ultimo messaggio del Lead a quel numero (`outside_service_window`,
 * regola di Meta: la finestra passa ai modelli approvati).
 */
export const WHATSAPP_REPLY_BLOCKS = ["do_not_contact", "whatsapp_opt_out", "outside_service_window"];
/**
 * Il messaggio del Lead a cui si risponde (`inReplyTo`, un messaggio in arrivo di questo Lead che chi risponde vede in
 * «Conversazioni»), l'identificativo della richiesta del browser (come «Invia il WhatsApp»: lo stesso clic due volte, o un nuovo
 * tentativo dopo una rete caduta, trovano lo stesso messaggio e niente parte due volte) e **uno** fra:
 * - `text`: le parole di chi risponde, soltanto dentro le 24 ore dall'ultimo messaggio del Lead a quel numero; il server mette in
 *   fondo la riga «scrivi STOP» dell'azienda (questione 421), e il testo con la riga sta nei 4096 caratteri di Meta;
 * - `templateKey`: un modello approvato da Meta, sempre (fuori dalle 24 ore è il solo modo), con le variabili del Lead.
 * `manual: true` è «Apri WhatsApp»: la risposta si prepara a mano, dal telefono di chi scrive (`prepared`, con il link `wa.me`), come
 * la preparazione di R1, per esempio quando il numero non è più pronto sul WhatsApp ufficiale; la finestra di 24 ore non conta.
 */
export const replyWhatsAppBodySchema = z
    .strictObject({
    inReplyTo: idSchema,
    clientRequestId: z.uuid(),
    text: multiLineTextSchema(WHATSAPP_REPLY_TEXT_MAX).optional(),
    templateKey: whatsAppTemplateKeySchema.optional(),
    manual: z.literal(true).optional(),
})
    .refine((body) => (body.text === undefined) !== (body.templateKey === undefined), { path: ["text"], message: "Either text or templateKey" });
/**
 * La risposta (200):
 * - `route`: la scelta di `whatsappRoute` per il numero che ha ricevuto il messaggio. Con `send` dal WhatsApp ufficiale il messaggio
 *   è partito verso Meta (`messageId`, `status` `queued` o `failed`, come «Invia il WhatsApp»); con `fallback_manual` (il numero non
 *   è più pronto, Gioia365 non lo ha più, l'azienda ha spento il WhatsApp ufficiale, il modello non è approvato) o con il numero
 *   passato a mano, senza `manual`, niente è stato scritto né mandato: `messageId` e `status` sono `null`, e la finestra dice il
 *   perché e offre «Apri WhatsApp» (`manual: true`);
 * - con `manual: true`: il messaggio preparato a mano (`status` `prepared`) e il suo `link` `wa.me`, che il browser apre; «Sì, l'ho
 *   inviato» come in R1 (`POST /api/whatsapp/messages/:messageId/declare-sent`). Senza `manual`, `link` è `null`;
 * - `dailyCapWarning`: soltanto per un modello (questione 413); un testo libero non conta nel tetto e non avvisa (questione 422).
 * La stessa richiesta (Lead e `clientRequestId`) risponde con lo stesso messaggio e lo stato di adesso.
 */
export const replyWhatsAppResponseSchema = z.object({
    route: whatsAppRouteSchema,
    messageId: z.string().nullable(),
    status: whatsAppMessageStatusSchema.nullable(),
    dailyCapWarning: z.boolean(),
    link: z.string().nullable(),
});
//# sourceMappingURL=whatsapp.js.map